import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import React, { useState } from "react";
import {
  SizingWrapper,
  type ISizingWrapperProps,
  type SizingWrapperStyle,
} from "../Wrapper";
import { type Control, Controller, type FieldValues } from "react-hook-form";
import type { FieldPath } from "react-hook-form/dist/types";

const PASSWORD_CHAR_CLASS = "0-9A-Za-z_!?#@$-";
const DISALLOWED_PASSWORD_REGEX = new RegExp(`[^${PASSWORD_CHAR_CLASS}]`, "g");

interface IProps<T extends FieldValues> extends ISizingWrapperProps {
  control: Control<T> | undefined;
  name: FieldPath<T>;
  id?: string; // id未指定の場合はTextFieldの内部でuseId()が使用される
  label?: string;
  required?: boolean;
  size?: SizingWrapperStyle;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoComplete?: string; // https://developer.mozilla.org/ja/docs/Web/HTML/Reference/Attributes/autocomplete
  center?: boolean;
}

const CTextFieldPassword = <T extends FieldValues>(
  props: IProps<T>
): React.ReactElement => {
  const {
    control,
    name,
    id,
    label = "パスワード",
    required = true,
    size,
    onKeyUp,
    disabled = false,
    autoComplete,
    center = false,
  } = props;

  const [isVisiblePassword, setIsVisiblePassword] = useState(false);

  /**
   * 入力済みかどうかを示すフラグ。
   *
   * 自動入力（オートフィル）または手動入力が行われた場合に `true` となります。
   *
   * ### 用途:
   * - 自動入力の検出（`onAnimationStart`と`onAnimationEnd` で更新）
   * - 未入力の場合、onAnimationStart→onAnimationEndの順で実行される
   * - 入力済みの場合、onAnimationStart→ユーザが入力または削除などInput内の文字を操作→onAnimationEndの順で実行される
   * - 手動入力の検出（`onChange` で更新）
   * - 入力済みかどうかによって、UI（特に背景色）を動的に切り替える
   * - 入力済みかどうかによって、`InputLabelProps` の `shrink` を制御
   * - 自動入力直後のフォーカスイン時、`IconButton` の背景色が白くなる現象の回避
   */
  const [hasFieldValue, setHasFieldValue] = useState(false);

  const toggleVisibilityPassword = (): void => {
    setIsVisiblePassword((isVisible) => !isVisible);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const handleChange = (
          event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ): void => {
          // 不許可文字がある場合は即除去
          if (DISALLOWED_PASSWORD_REGEX.test(event.target.value)) {
            event.target.value = event.target.value.replace(
              DISALLOWED_PASSWORD_REGEX,
              ""
            );
          }
          field.onChange(event.target.value);
        };

        return (
          <SizingWrapper size={size} center={center}>
            <TextField
              id={id}
              label={label}
              type={isVisiblePassword ? "text" : "password"}
              value={field.value}
              onChange={(event) => {
                setHasFieldValue(event.target.value.length > 0);
                handleChange(event);
              }}
              onFocus={(event) => {
                setHasFieldValue(event.target.value.length > 0);
              }}
              onKeyUp={onKeyUp}
              onBlur={field.onBlur}
              disabled={disabled}
              required={required}
              fullWidth
              error={Boolean(fieldState.error)}
              autoComplete={autoComplete}
              helperText={
                fieldState.error?.types
                  ? // criteriaMode: "all" の場合
                    // 複数のエラーメッセージを同時に表示するための処理
                    Object.values(fieldState.error.types)
                      .flat()
                      .map((msg, i, arr) => (
                        <React.Fragment key={i}>
                          {msg}
                          {i < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                  : // criteriaMode: "firstError"（デフォルト） の場合
                    fieldState.error?.message
              }
              InputLabelProps={{
                // 自動入力も含めて、入力時にlabelを上に移動
                shrink: hasFieldValue,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  // 入力済みの際にアイコンが浮かないように
                  background: hasFieldValue ? "#E9F0FD" : undefined,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleVisibilityPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {isVisiblePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                onAnimationStart: (e) => {
                  if (e.animationName.includes("mui-auto-fill")) {
                    setHasFieldValue(true);
                  }
                },
                onAnimationEnd: (e) => {
                  if (e.animationName.includes("mui-auto-fill")) {
                    const target = e.target as HTMLInputElement;
                    if (target.value.length === 0) {
                      setHasFieldValue(false);
                    }
                  }
                },
              }}
            />
          </SizingWrapper>
        );
      }}
    />
  );
};

export { CTextFieldPassword };
